/* eslint-disable react/prop-types */
import React, { ReactElement, useMemo, useState } from "react";
import { ExportImportTransaction, ExportImportType } from "../../models/Transaction";
import { MantineReactTable, MRT_ColumnDef, MRT_SortingState, useMantineReactTable } from "mantine-react-table";
import { convertFromWei, formatAddress, formatAmountWithLabel } from "../../utils/formatter";
import { formatDate } from "../../utils/formatTime";
import styles from "./AddressDetailsTabs.module.scss";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { QueryKey } from "../../models/enums";
import { ExportImportTransactionsResponse } from "../../models/Response";
import { IndexerService } from "../../services/indexer.service";
import { calculateAmount } from "../../utils/exportImportHelper";
import { Text } from "@mantine/core";
import TableFilterDropdown, { OptionValue } from "../tableFilterDropdown/TableFilterDropdown";
import CopyTextComponent from "../copyText/CopyText.component";
import { useStore } from "@tanstack/react-store";
import { IStore, store } from "../../services/store.service";

const cChainTransactionTypes: OptionValue[] = [
  { value: "", label: "All types" },
  { value: ExportImportType.EXPORT, label: "Export" },
  { value: ExportImportType.IMPORT, label: "Import" },
];

export default function CChainTabComponent({ active }: {
  active: boolean;
}): ReactElement {
  const timeZone = useStore<IStore, IStore["timeZone"]>(store, (state) => state.timeZone).selected;
  const { address } = useParams();
  const navigate = useNavigate();

  //store pagination state in your own state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10, //customize the default page size
  });
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [selectedType, setSelectedType] = useState<ExportImportType | null>(
    null,
  );

  const { data: importExportTransactions } = useQuery({
    queryKey: [
      QueryKey.importExportTransactionsList,
      address,
      pagination.pageIndex,
      pagination.pageSize,
      selectedType,
      sorting,
    ],
    queryFn: (): Promise<ExportImportTransactionsResponse> => {
      const timestampSorting = sorting.find((item) => item.id === "timestamp");
      return IndexerService.getCChainTransactions(
        address!,
        null,
        null,
        pagination.pageIndex * pagination.pageSize,
        pagination.pageSize,
        timestampSorting ? (timestampSorting.desc ? "desc" : "asc") : undefined,
        selectedType,
      );
    },
    enabled: !!address && active,
    staleTime: 10000,
  });

  //should be memoized or stable
  const columns = useMemo<MRT_ColumnDef<ExportImportTransaction>[]>(
    () => [
      {
        accessorKey: "tx_id",
        header: "Id",
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell, row }) => (
          <CopyTextComponent
            primaryColor={false}
            text={formatAddress(cell.getValue<string>())}
            value={cell.getValue<string>()}
            link={`/tx/c-chain/${row.original.tx_id}`}
            size={"14px"}
            weight={"500"}
          ></CopyTextComponent>
        ),
      },
      {
        accessorFn: (tx) => {
          return tx.type; //TODO: format label
        },
        header: "Type",
        Header: () => (
          <>
            <Text fw={500}>Type</Text>
            <TableFilterDropdown
              values={cChainTransactionTypes}
              setSelectedValue={(item) => {
                setSelectedType(item ? (item as ExportImportType) : null);
              }}
              selectedValue={selectedType}
            />
          </>
        ),
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        enableSorting: false,
        enableColumnFilter: true,
        filterVariant: "select",

        size: 150,
        Cell: ({ cell }) => (
          <span style={{ textTransform: "capitalize" }}>
            {cell.getValue<string>()}
          </span>
        ),
      },
      {
        id: "amount", //normal accessorKey
        header: "Amount",
        // size: 100,
        mantineTableHeadCellProps: {
          align: "right",
        },
        mantineTableBodyCellProps: {
          align: "right",
        },
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <>{formatAmountWithLabel(convertFromWei(calculateAmount(row.original)), false, 2, true)}</>
        ),
      },
      {
        accessorKey: "timestamp",
        header: "Timestamp",
        size: 250,
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => (
          <>
            {cell.getValue<string>() === null ? (
              <span> No date provided</span>
            ) : (
              <span>
                {
                  formatDate(new Date(+cell.getValue<string>() * 1000), timeZone)
                    .formattedDateTime
                }
                &nbsp;
                <span className={styles.unitLabel}>
                  {formatDate(new Date(+cell.getValue<string>() * 1000), timeZone).utcOffset}
                </span>
              </span>
            )}
          </>
        ),
      },
    ],
    [importExportTransactions?.results, timeZone],
  );

  const table = useMantineReactTable({
    columns: importExportTransactions ? columns : [],
    data: importExportTransactions?.results || [], //must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    mantineTableProps: {
      style: {
        tableLayout: "fixed",
        fontWeight: 500,
        fontSize: "var(--module-size-14)",
      },
    },
    mantineTableHeadCellProps: {
      style: {
        fontWeight: 500,
        fontSize: 15,
      },
    },
    state: {
      isLoading: !importExportTransactions,
      pagination,
      sorting,
    },
    mantinePaperProps: {
      withBorder: false,
      shadow: "none",
    },
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableFilters: false,
    initialState: {
      density: "xs",
    },
    manualPagination: true,
    manualSorting: true,
    rowCount: importExportTransactions?.count,
    onPaginationChange: setPagination, //hoist pagination state to your state when it changes internally
    onSortingChange: setSorting,
    mantinePaginationProps: {
      showRowsPerPage: false,
      withEdges: false,
    },
  });

  return <MantineReactTable table={table} />;
}
